<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { PInput, IconCrossSm as CrossIcon } from "@profeat/ui-kit";
import { onClickOutside, onKeyStroke, watchDebounced } from "@vueuse/core";

import { customerConstants } from "~/components/customers/helper";
import { ContactField, SystemFieldType } from "~/entities/contacts";
import { FilterBuilder } from "~/components/customers/filter-builder";

const { DEFAULT_FILTER_DEBOUNCE } = customerConstants;
const props = defineProps<{
  field: ContactField;
  filterBuilder: FilterBuilder;
  refWrap: HTMLElement;
}>();
const emit = defineEmits<{
  (event: "applyFilter", visible?: boolean, withoutCall?: boolean): void;
  (event: "resetFilter", withoutCall?: boolean): void;
  (event: "clickOutside"): void;
  (event: "update:changed", val: boolean): void;
}>();

const filterRef = ref(null);
const enterPressed = ref(false);
const filterValue = ref("");
const isUnmounted = ref(false);

onBeforeUnmount(() => {
  isUnmounted.value = true;
});

const isPhoneFilter = computed(() => props.field.type === SystemFieldType.Phone);
const mountedFilterValue = computed(() =>
  props.filterBuilder.getFilterById(props.field.id)
);

const clearFilter = () => {
  filterValue.value = "";
  emit("resetFilter");
};
const setToFilter = () => {
  if (
    mountedFilterValue.value === filterValue.value ||
    enterPressed.value ||
    (mountedFilterValue.value === null && filterValue.value === "")
  )
    return;

  const hasFilter = !!props.filterBuilder.getFilterById(props.field.id);
  if (hasFilter && !filterValue.value) {
    emit("resetFilter");
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  props.filterBuilder.find(props.field.id, filterValue.value!);
  emit("applyFilter");
};

watchDebounced(
  filterValue,
  () => {
    if (isUnmounted.value) return;
    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE }
);

onKeyStroke(["Enter"], () => {
  setToFilter();
  enterPressed.value = true;
});
onClickOutside(
  () => props.refWrap,
  () => {
    setToFilter();
    emit("clickOutside");
  }
);
onMounted(() => {
  const hasFilter = props.filterBuilder.getFilterById(props.field.id);

  if (hasFilter) {
    filterValue.value = hasFilter as string;
  }
});
watch(filterValue, (val) => {
  emit("update:changed", !!val);
});
</script>

<template>
  <div class="string-filter" ref="filterRef">
    <PInput
      v-model="filterValue"
      text-size="small"
      size="medium"
      :type="isPhoneFilter ? 'tel' : 'text'"
      autofocus
      :placeholder="$t('customers.filter.string.placeholder')"
    >
      <template #right>
        <CrossIcon v-if="!!filterValue" class="icon" @click="clearFilter" />
      </template>
    </PInput>
  </div>
</template>

<style lang="scss" scoped>
.string-filter {
  :deep(.icon) {
    cursor: pointer;

    path {
      fill: var(--common-asphalt, #91a0ae);
    }
  }
}
</style>
